import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  EmptyState,
  Icon,
  SegmentedControl,
  SettingGroup,
  SettingRow,
  Sheet,
  Skeleton,
} from '@astral/ui';
import {
  BUILT_IN_CATEGORIES,
  type BuiltInCategory,
  type ProjectDTO,
  type ProjectStatus,
} from '@astral/shared';
import { api } from '../lib/api.js';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';
import { SubHeader } from '../components/SubHeader.js';
import { useToast } from '../providers/ToastProvider.js';
import { useProjects } from '../hooks/useProjects.js';
import { ProjectCard } from '../components/ProjectCard.js';
import { ConfirmSheet } from '../components/ConfirmSheet.js';
import { CategoryManagerSheet } from '../components/CategoryManagerSheet.js';
import { useCustomCategories } from '../hooks/useCustomCategories.js';

type StatusFilter = 'all' | ProjectStatus;

/**
 * The active category filter.
 *
 * Built-in and custom are SEPARATE variants, never one overloaded string: a
 * custom id could otherwise be read as a built-in id (or the reverse), which is
 * exactly the ambiguity the two storage columns exist to avoid.
 *
 * There is deliberately no filter for LEGACY values — retired categories are
 * not selectable, and those projects are reached through "All".
 */
type CategoryFilter =
  | { kind: 'all' }
  | { kind: 'builtin'; id: BuiltInCategory }
  | { kind: 'custom'; id: string };

/** Projects list: search, status + category filters, and per-project actions. */
export function ProjectsScreen(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>({ kind: 'all' });
  const [manageOpen, setManageOpen] = useState(false);
  const { categories: customCategories, reload: reloadCategories } = useCustomCategories();
  const [moreProject, setMoreProject] = useState<ProjectDTO | null>(null);
  const [deleteProject, setDeleteProject] = useState<ProjectDTO | null>(null);
  const [busy, setBusy] = useState(false);

  const { projects, loading, error, reload } = useProjects({
    search: search.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter.kind === 'builtin' ? categoryFilter.id : undefined,
    customCategoryId: categoryFilter.kind === 'custom' ? categoryFilter.id : undefined,
    limit: 50,
  });

  const withBusy = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const duplicate = (project: ProjectDTO): void => {
    void withBusy(async () => {
      await api.duplicateProject(project.id);
      toast.show(t('toast.duplicated'), 'success');
      setMoreProject(null);
      reload();
    });
  };

  const setStatus = (project: ProjectDTO, status: ProjectStatus, toastKey: TranslationKey): void => {
    void withBusy(async () => {
      await api.updateProject(project.id, { status });
      toast.show(t(toastKey), 'success');
      setMoreProject(null);
      reload();
    });
  };

  const confirmDelete = (): void => {
    if (!deleteProject) return;
    void withBusy(async () => {
      await api.deleteProject(deleteProject.id);
      toast.show(t('toast.deleted'), 'default');
      setDeleteProject(null);
      reload();
    });
  };

  const emptyTitle = search
    ? t('projects.emptySearch')
    : statusFilter === 'ARCHIVED'
      ? t('projects.emptyArchived')
      : t('projects.empty');

  return (
    <div className="screen screen--with-nav">
      <SubHeader
        title={t('projects.title')}
        trailing={
          <Button size="sm" leftIcon={<Icon name="plus" size={18} />} onClick={() => navigate('/projects/new')}>
            {t('projects.newProject')}
          </Button>
        }
      />

      <div className="searchbar">
        <Icon name="search" size={18} />
        <input
          value={search}
          placeholder={t('projects.searchPlaceholder')}
          onChange={(event) => setSearch(event.target.value)}
        />
        {search && (
          <button
            className="muted"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setSearch('')}
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>

      <SegmentedControl<StatusFilter>
        value={statusFilter}
        onChange={setStatusFilter}
        options={[
          { value: 'all', label: t('projects.all') },
          { value: 'DRAFT', label: t('projects.status.DRAFT') },
          { value: 'ACTIVE', label: t('projects.status.ACTIVE') },
          { value: 'ARCHIVED', label: t('projects.status.ARCHIVED') },
        ]}
      />

      {/* Category filter — All, the six built-ins in order, then the user's own. */}
      <div className="chips">
        <button
          type="button"
          className={`chip${categoryFilter.kind === 'all' ? ' chip--active' : ''}`}
          onClick={() => setCategoryFilter({ kind: 'all' })}
        >
          {t('projects.allCategories')}
        </button>
        {BUILT_IN_CATEGORIES.map((value) => (
          <button
            key={value}
            type="button"
            className={`chip${
              categoryFilter.kind === 'builtin' && categoryFilter.id === value ? ' chip--active' : ''
            }`}
            onClick={() => setCategoryFilter({ kind: 'builtin', id: value })}
          >
            {t(`categories.${value}` as TranslationKey)}
          </button>
        ))}
        {customCategories.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`chip${
              categoryFilter.kind === 'custom' && categoryFilter.id === entry.id ? ' chip--active' : ''
            }`}
            onClick={() => setCategoryFilter({ kind: 'custom', id: entry.id })}
          >
            {entry.name}
          </button>
        ))}
        {/* Compact management entry point, in the same chip row. */}
        <button
          type="button"
          className="chip"
          aria-label={t('categoryManager.manage')}
          onClick={() => setManageOpen(true)}
        >
          <Icon name="plus" size={14} />
        </button>
      </div>

      {loading ? (
        <div className="stack">
          <Skeleton height={128} radius={20} />
          <Skeleton height={128} radius={20} />
        </div>
      ) : error ? (
        <EmptyState
          icon={<Icon name="folder" size={28} />}
          title={t('errors.loadFailed')}
          action={<Button onClick={reload}>{t('common.retry')}</Button>}
        />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<Icon name="folder" size={28} />}
          title={emptyTitle}
          description={search || statusFilter !== 'all' ? undefined : t('projects.emptyHint')}
          action={
            !search && (
              <Button leftIcon={<Icon name="plus" size={18} />} onClick={() => navigate('/projects/new')}>
                {t('projects.newProject')}
              </Button>
            )
          }
        />
      ) : (
        <div className="stack">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={() => navigate(`/projects/${project.id}`)}
              onMore={() => setMoreProject(project)}
            />
          ))}
        </div>
      )}

      {/* Per-project actions */}
      <Sheet open={Boolean(moreProject)} onClose={() => setMoreProject(null)} title={moreProject?.title}>
        {moreProject && (
          <SettingGroup>
            <SettingRow
              icon={<Icon name="edit" size={18} />}
              title={t('projects.open')}
              onClick={() => {
                navigate(`/projects/${moreProject.id}`);
                setMoreProject(null);
              }}
            />
            <SettingRow
              icon={<Icon name="copy" size={18} />}
              title={t('common.duplicate')}
              onClick={() => duplicate(moreProject)}
            />
            {moreProject.status === 'ARCHIVED' ? (
              <SettingRow
                icon={<Icon name="history" size={18} />}
                title={t('projects.restore')}
                onClick={() => setStatus(moreProject, 'ACTIVE', 'toast.restored')}
              />
            ) : (
              <SettingRow
                icon={<Icon name="archive" size={18} />}
                title={t('projects.archive')}
                onClick={() => setStatus(moreProject, 'ARCHIVED', 'toast.archived')}
              />
            )}
            <SettingRow
              icon={<Icon name="trash" size={18} />}
              title={t('common.delete')}
              onClick={() => {
                setDeleteProject(moreProject);
                setMoreProject(null);
              }}
            />
          </SettingGroup>
        )}
      </Sheet>

      <CategoryManagerSheet
        open={manageOpen}
        categories={customCategories}
        onClose={() => setManageOpen(false)}
        onChanged={({ deletedId }) => {
          /*
           * If the filter that was showing has just been deleted, fall back to
           * All — otherwise the list would silently filter on an id that no
           * longer exists and appear empty.
           */
          if (deletedId && categoryFilter.kind === 'custom' && categoryFilter.id === deletedId) {
            setCategoryFilter({ kind: 'all' });
          }
          reloadCategories();
          reload();
        }}
      />

      <ConfirmSheet
        open={Boolean(deleteProject)}
        danger
        loading={busy}
        title={t('projects.deleteConfirmTitle')}
        message={t('projects.deleteConfirmBody', { title: deleteProject?.title ?? '' })}
        confirmLabel={t('common.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteProject(null)}
      />
    </div>
  );
}
